import React, { useEffect, useRef, useState } from "react";
import {
  FolderClosed,
  FileText,
  Image as ImageIcon,
  Video as VideoIcon,
  Music as MusicIcon,
  FileSpreadsheet,
  Presentation,
  FileType2,
  File as FileGeneric,
  Plus,
  Trash2,
  ChevronRight,
  Search,
  Sparkles,
  Loader2,
  Home,
  Upload,
  Download,
  Pencil,
  FolderInput,
  Share2,
  Lock,
  Users,
  X,
} from "lucide-react";
import { filesApi, aiQuick } from "../api";
import { useAuth } from "../AuthContext";

// --- helpers ----------------------------------------------------------------
const fmtBytes = (n) => {
  if (!n && n !== 0) return "";
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(2)} MB`;
};

// Color overrides for the auto-created system folders so the root view matches
// the design spec (Images=violet, Audio=amber, Video=blue, Workspace=gold).
const SYSTEM_FOLDER_TINTS = {
  Images: "#a78bfa",
  Audio: "#f59e0b",
  Video: "#3b82f6",
  Workspace: "#eab308",
  // Workspace sub-folders (the four "office" buckets) — match the file-type
  // tints we use for individual documents inside them.
  Docs: "#60a5fa",
  PDFs: "#ef4444",
  Sheets: "#22c55e",
  Slides: "#f97316",
};

// Name-based icon overrides for well-known buckets. Lets us treat the four
// Workspace sub-folders the same as the system-level mega-tiles even though
// they aren't flagged `system` in the DB.
const NAMED_FOLDER_ICONS = {
  Images: () => ImageIcon,
  Audio: () => MusicIcon,
  Video: () => VideoIcon,
  Docs: () => FileText,
  PDFs: () => FileType2,
  Sheets: () => FileSpreadsheet,
  Slides: () => Presentation,
};

const iconFor = (item) => {
  if (item.type === "folder") {
    const named = NAMED_FOLDER_ICONS[item.name];
    if (named) return named();
    return FolderClosed;
  }
  const m = (item.mime || "").toLowerCase();
  if (m.startsWith("image/")) return ImageIcon;
  if (m.startsWith("video/")) return VideoIcon;
  if (m.startsWith("audio/")) return MusicIcon;
  if (m.includes("spreadsheet") || m.includes("excel") || m === "text/csv") return FileSpreadsheet;
  if (m.includes("presentation") || m.includes("powerpoint")) return Presentation;
  if (m === "application/pdf") return FileType2;
  if (m.startsWith("text/") || m.includes("word") || m.includes("document")) return FileText;
  return FileGeneric;
};

const colorFor = (item) => {
  if (item.type === "folder") {
    if (SYSTEM_FOLDER_TINTS[item.name]) return SYSTEM_FOLDER_TINTS[item.name];
    return "#a78bfa";
  }
  const m = (item.mime || "").toLowerCase();
  if (m.startsWith("image/")) return "#60a5fa";
  if (m.startsWith("video/")) return "#fb7185";
  if (m.startsWith("audio/")) return "#34d399";
  if (m.includes("spreadsheet") || m === "text/csv") return "#22c55e";
  if (m.includes("presentation")) return "#f59e0b";
  if (m === "application/pdf") return "#ef4444";
  return "#e5e7eb";
};

export default function Files() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [path, setPath] = useState([{ id: null, name: "Home" }]);
  const [q, setQ] = useState("");
  const [aiSearchResult, setAiSearchResult] = useState("");
  const [aiBusy, setAiBusy] = useState(false);
  const [openFile, setOpenFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadPct, setUploadPct] = useState(0);
  const [uploadStatus, setUploadStatus] = useState(null); // { name, size, mime, dest }
  const [moveTarget, setMoveTarget] = useState(null); // item being moved
  const [moveDirs, setMoveDirs] = useState([]); // folder choices
  const [shareTarget, setShareTarget] = useState(null);
  const uploadInputRef = useRef(null);

  const currentParent = path[path.length - 1].id;
  const isRoot = path.length === 1;
  // Mega-tiles also render for the four "office" buckets inside the Workspace folder.
  const isInWorkspace = path.length === 2 && path[1].name === "Workspace";

  // Track all user files for child counts on the mega-tiles.
  const [allItems, setAllItems] = useState([]);
  // Recursive count of FILES (not sub-folders) under a folder. Sub-folder
  // entries themselves don't count toward "items" — only their leaf contents.
  const countIn = (folderId) => {
    if (!folderId) return 0;
    const directChildren = allItems.filter((f) => f.parent === folderId);
    let total = 0;
    for (const child of directChildren) {
      if (child.type === "folder") total += countIn(child.id);
      else total += 1;
    }
    return total;
  };

  const load = async () => {
    const list = await filesApi.list(currentParent);
    setItems(list);
    // Always refresh the full tree so mega-tile counts stay accurate at
    // Home AND inside Workspace.
    try {
      const all = await filesApi.all();
      setAllItems(all);
    } catch { /* ignore */ }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentParent]);

  const newFolder = async () => {
    const name = prompt("Folder name");
    if (!name) return;
    await filesApi.create({ name, type: "folder", parent: currentParent });
    load();
  };

  const newFile = async () => {
    const name = prompt("File name (e.g., notes.txt)");
    if (!name) return;
    await filesApi.create({
      name,
      type: "file",
      parent: currentParent,
      content: "",
      mime: "text/plain",
    });
    load();
  };

  const onPickUpload = () => uploadInputRef.current?.click();

  const handleFiles = async (fileList) => {
    if (!fileList || fileList.length === 0) return;
    setUploading(true);
    setUploadPct(0);
    try {
      for (let i = 0; i < fileList.length; i++) {
        const file = fileList[i];
        // Skip files > 10MB with a warning
        if (file.size > 10 * 1024 * 1024) {
          alert(`"${file.name}" is larger than 10 MB and was skipped.`);
          continue;
        }
        // Predict the destination folder from MIME so the user sees real-time routing.
        const m = (file.type || "").toLowerCase();
        let dest = "Workspace/Docs";
        if (m.startsWith("image/")) dest = "Images";
        else if (m.startsWith("audio/")) dest = "Audio";
        else if (m.startsWith("video/")) dest = "Video";
        else if (m === "application/pdf") dest = "Workspace/PDFs";
        else if (m.includes("spreadsheet") || m === "text/csv") dest = "Workspace/Sheets";
        else if (m.includes("presentation")) dest = "Workspace/Slides";
        setUploadStatus({ name: file.name, size: file.size, mime: file.type, dest });
        setUploadPct(0);
        await filesApi.upload(file, currentParent, (e) => {
          if (!e.total) return;
          setUploadPct(Math.round((e.loaded / e.total) * 100));
        });
      }
      await load();
    } catch (err) {
      console.error(err);
      alert("Upload failed: " + (err?.response?.data?.detail || err.message));
    } finally {
      setUploading(false);
      setUploadPct(0);
      // Keep the card visible for 1s after completion so user sees "100%".
      setTimeout(() => setUploadStatus(null), 1200);
      if (uploadInputRef.current) uploadInputRef.current.value = "";
    }
  };

  const onDownload = (item) => {
    const url = filesApi.downloadUrl(item.id);
    const a = document.createElement("a");
    a.href = url;
    a.download = item.name;
    document.body.appendChild(a);
    a.click();
    a.remove();
  };

  const onRename = async (item) => {
    const next = prompt("Rename to:", item.name);
    if (!next || next === item.name) return;
    await filesApi.rename(item.id, next.trim());
    load();
  };

  const openMove = async (item) => {
    // Fetch all folders to choose from.
    const all = await filesApi.all();
    const folders = all.filter((f) => f.type === "folder" && f.id !== item.id);
    setMoveDirs(folders);
    setMoveTarget(item);
  };

  const doMove = async (parentId) => {
    if (!moveTarget) return;
    try {
      await filesApi.move(moveTarget.id, parentId);
      setMoveTarget(null);
      setMoveDirs([]);
      load();
    } catch (err) {
      alert("Move failed: " + (err?.response?.data?.detail || err.message));
    }
  };

  const remove = async (item) => {
    if (!confirm(`Delete "${item.name}"?`)) return;
    await filesApi.remove(item.id);
    load();
  };

  const semanticSearch = async () => {
    if (!q.trim()) {
      setAiSearchResult("");
      return;
    }
    setAiBusy(true);
    setAiSearchResult("");
    const all = await filesApi.all();
    const list = all
      .map(
        (f) =>
          `- ${f.type}: ${f.name}${f.mime ? ` [${f.mime}]` : ""}${
            f.content ? ` ("${(f.content || "").slice(0, 120)}")` : ""
          }`
      )
      .join("\n");
    try {
      const r = await aiQuick(
        `User is searching their virtual files for: "${q}". Below is the file list. Find the most relevant files and explain why.\n\n${list}`,
        "You are an AI file search assistant. Be concise."
      );
      setAiSearchResult(r.text);
    } catch {
      setAiSearchResult("Search failed.");
    }
    setAiBusy(false);
  };

  // Drag-and-drop upload
  const onDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "copy";
  };
  const onDrop = (e) => {
    e.preventDefault();
    const files = Array.from(e.dataTransfer?.files || []);
    if (files.length) handleFiles(files);
  };

  return (
    <div
      className="h-full flex flex-col relative"
      data-testid="app-files"
      onDragOver={onDragOver}
      onDrop={onDrop}
    >
      {/* Top bar: breadcrumbs + actions */}
      <div className="flex items-center justify-between p-3 border-b border-white/10 flex-wrap gap-2">
        <div className="flex items-center gap-1 text-sm">
          {path.map((p, i) => (
            <span key={i} className="flex items-center gap-1">
              <button
                onClick={() => setPath(path.slice(0, i + 1))}
                className="px-2 py-1 rounded hover:bg-white/5 text-white/80"
              >
                {i === 0 ? <Home size={12} className="inline mr-1" /> : null}
                {p.name}
              </button>
              {i < path.length - 1 && (
                <ChevronRight size={12} className="text-white/40" />
              )}
            </span>
          ))}
        </div>
        <div className="flex gap-2">
          <input
            ref={uploadInputRef}
            data-testid="files-upload-input"
            type="file"
            multiple
            className="hidden"
            onChange={(e) => handleFiles(e.target.files)}
          />
          <button
            data-testid="files-upload"
            onClick={onPickUpload}
            disabled={uploading}
            className="btn-ghost text-xs flex items-center gap-1"
          >
            {uploading ? (
              <Loader2 size={12} className="animate-spin" />
            ) : (
              <Upload size={12} />
            )}{" "}
            {uploading ? `${uploadPct}%` : "Upload"}
          </button>
          <button
            data-testid="files-new-folder"
            onClick={newFolder}
            className="btn-ghost text-xs flex items-center gap-1"
          >
            <Plus size={12} /> Folder
          </button>
          <button
            data-testid="files-new-file"
            onClick={newFile}
            className="btn-ghost text-xs flex items-center gap-1"
          >
            <Plus size={12} /> File
          </button>
        </div>
      </div>

      {/* AI search */}
      <div className="flex items-center gap-2 px-3 py-2 border-b border-white/10">
        <Search size={13} className="text-white/40" />
        <input
          data-testid="files-search"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Semantic search across all files…"
          className="flex-1 bg-transparent outline-none text-sm"
          onKeyDown={(e) => e.key === "Enter" && semanticSearch()}
        />
        <button
          data-testid="files-ai-search"
          onClick={semanticSearch}
          className="btn-ghost text-xs flex items-center gap-1"
        >
          {aiBusy ? (
            <Loader2 size={12} className="animate-spin" />
          ) : (
            <Sparkles size={12} color="#a78bfa" />
          )}{" "}
          AI Search
        </button>
      </div>

      {aiSearchResult && (
        <div className="p-3 border-b border-white/10 bg-white/3 text-xs whitespace-pre-wrap text-white/80">
          {aiSearchResult}
        </div>
      )}

      {/* File grid */}
      <div
        className={
          "flex-1 overflow-y-auto p-4 content-start grid gap-3 " +
          ((isRoot || isInWorkspace) ? "grid-cols-3 sm:grid-cols-4 md:grid-cols-6 auto-rows-fr" : "grid-cols-4")
        }
      >
        {items.map((it) => {
          const Icon = iconFor(it);
          const tint = colorFor(it);
          const isFolder = it.type === "folder";
          const useMegaTile = (isRoot || isInWorkspace) && isFolder;
          const childCount = useMegaTile ? countIn(it.id) : 0;
          const baseProps = {
            "data-testid": `file-item-${it.id}`,
            onDoubleClick: () => {
              if (isFolder) setPath([...path, { id: it.id, name: it.name }]);
              else setOpenFile(it);
            },
          };
          if (useMegaTile) {
            return (
              <div
                key={it.id}
                {...baseProps}
                className="group relative flex flex-col items-center justify-between cursor-pointer transition hover:-translate-y-0.5"
                style={{
                  aspectRatio: "1 / 1.25",
                  minHeight: 130,
                  maxHeight: 170,
                  padding: "14px 8px 10px",
                  background: `linear-gradient(180deg, ${tint}14 0%, rgba(248,250,252,0.92) 55%, #ffffff 100%)`,
                  border: `1px solid ${tint}88`,
                  boxShadow: `0 0 22px ${tint}48, 0 0 0 1px ${tint}22 inset, 0 12px 28px rgba(0,0,0,0.5)`,
                  borderRadius: 4,
                }}
              >
                {/* faint scanline overlay */}
                <div
                  className="absolute inset-0 pointer-events-none opacity-30"
                  style={{
                    background:
                      "repeating-linear-gradient(0deg, rgba(255,255,255,0.02) 0px, rgba(255,255,255,0.02) 1px, transparent 1px, transparent 3px)",
                    mixBlendMode: "overlay",
                  }}
                />

                {/* Neon icon */}
                <div className="flex-1 flex items-center justify-center w-full">
                  <Icon
                    size={62}
                    strokeWidth={1.4}
                    color={tint}
                    style={{
                      filter: `drop-shadow(0 0 12px ${tint}cc) drop-shadow(0 0 24px ${tint}66)`,
                    }}
                  />
                </div>

                {/* Name (serif-italic, glowing) */}
                <div
                  className="text-center w-full px-1 truncate"
                  style={{
                    fontFamily: 'Georgia, "Times New Roman", serif',
                    fontStyle: "italic",
                    fontSize: 18,
                    color: "rgba(255,255,255,0.92)",
                    textShadow: `0 0 8px ${tint}99, 0 0 18px ${tint}55`,
                    letterSpacing: "0.01em",
                  }}
                >
                  {it.name}
                </div>

                {/* count */}
                <div
                  className="mt-1 mono text-[10.5px] tracking-[0.16em]"
                  style={{
                    color: `${tint}d0`,
                    textShadow: `0 0 6px ${tint}88`,
                  }}
                >
                  {childCount} items
                </div>

                {/* lock chip for system folders (subtle, top-left) */}
                {it.system && (
                  <div
                    className="absolute top-2 left-2 flex items-center gap-1 text-[9px] mono uppercase tracking-[0.22em] px-1.5 py-0.5 rounded"
                    style={{
                      background: "#f8fafc",
                      border: `1px solid ${tint}55`,
                      color: `${tint}d0`,
                    }}
                  >
                    <Lock size={9} /> system
                  </div>
                )}

                {/* shared chip (top-right) */}
                {(it.shared_with || []).length > 0 && (
                  <div
                    className="absolute top-2 right-2 flex items-center gap-1 text-[9px] mono uppercase tracking-[0.22em] px-1.5 py-0.5 rounded"
                    style={{
                      background: "#f8fafc",
                      border: "1px solid rgba(167,139,250,0.55)",
                      color: "#c7c1ff",
                    }}
                  >
                    <Users size={9} /> {it.shared_with.length}
                  </div>
                )}

                {/* Hover share/rename/delete for non-system root folders */}
                {!it.system && (it.owner === user?.id || !it.owner) && (
                  <div className="absolute top-2 right-2 flex gap-0.5 opacity-0 group-hover:opacity-100 transition">
                    <button
                      data-testid={`file-share-${it.id}`}
                      title="Share"
                      onClick={(e) => { e.stopPropagation(); setShareTarget(it); }}
                      className="p-1 rounded bg-black/40 hover:bg-white/10"
                    >
                      <Share2 size={11} className="text-[#a78bfa]" />
                    </button>
                    <button
                      data-testid={`file-rename-${it.id}`}
                      title="Rename"
                      onClick={(e) => { e.stopPropagation(); onRename(it); }}
                      className="p-1 rounded bg-black/40 hover:bg-white/10"
                    >
                      <Pencil size={11} className="text-white/70" />
                    </button>
                    <button
                      data-testid={`file-delete-${it.id}`}
                      title="Delete"
                      onClick={(e) => { e.stopPropagation(); remove(it); }}
                      className="p-1 rounded bg-black/40 hover:bg-white/10"
                    >
                      <Trash2 size={11} className="text-red-400" />
                    </button>
                  </div>
                )}
              </div>
            );
          }
          // Fall-through: original compact tile for subfolder contents / files at root
          return (
            <div
              key={it.id}
              {...baseProps}
              className="app-grid-cell flex flex-col items-center gap-2 relative group"
            >
              <Icon size={32} strokeWidth={1.2} color={tint} />
              <div className="text-xs truncate w-full text-center flex items-center justify-center gap-1">
                {it.system && <Lock size={9} className="text-white/40" />}
                {it.name}
              </div>
              {it.size ? (
                <div className="text-[10px] text-white/40 mono">{fmtBytes(it.size)}</div>
              ) : null}
              {/* Shared/perm indicator */}
              {(it.shared_with || []).length > 0 && (it.owner === user?.id || !it.owner) && (
                <div className="text-[9px] mono uppercase tracking-widest text-[#a78bfa] flex items-center gap-1">
                  <Users size={9} /> shared · {it.shared_with.length}
                </div>
              )}
              {it.owner && user?.id && it.owner !== user.id && (it.shared_with || []).some(s => s.address === user.address) && (
                <div className="text-[9px] mono uppercase tracking-widest text-[#34d399] flex items-center gap-1">
                  <Users size={9} /> {(it.shared_with.find(s => s.address === user.address) || {}).perm === "edit" ? "edit" : "read-only"}
                </div>
              )}

              {/* Per-item action buttons (show on hover) */}
              <div className="absolute top-1 right-1 flex gap-0.5 opacity-0 group-hover:opacity-100 transition">
                {it.type === "file" && (
                  <button
                    data-testid={`file-download-${it.id}`}
                    title="Download"
                    onClick={(e) => {
                      e.stopPropagation();
                      onDownload(it);
                    }}
                    className="p-1 rounded hover:bg-white/10"
                  >
                    <Download size={11} className="text-white/70" />
                  </button>
                )}
                {!it.system && (it.owner === user?.id || !it.owner) && (
                  <button
                    data-testid={`file-share-${it.id}`}
                    title="Share with team"
                    onClick={(e) => {
                      e.stopPropagation();
                      setShareTarget(it);
                    }}
                    className="p-1 rounded hover:bg-white/10"
                  >
                    <Share2 size={11} className="text-[#a78bfa]" />
                  </button>
                )}
                {!it.system && (
                  <button
                    data-testid={`file-rename-${it.id}`}
                    title="Rename"
                    onClick={(e) => {
                      e.stopPropagation();
                      onRename(it);
                    }}
                    className="p-1 rounded hover:bg-white/10"
                  >
                    <Pencil size={11} className="text-white/70" />
                  </button>
                )}
                {!it.system && (
                  <button
                    data-testid={`file-move-${it.id}`}
                    title="Move"
                    onClick={(e) => {
                      e.stopPropagation();
                      openMove(it);
                    }}
                    className="p-1 rounded hover:bg-white/10"
                  >
                    <FolderInput size={11} className="text-white/70" />
                  </button>
                )}
                {!it.system && (
                  <button
                    data-testid={`file-delete-${it.id}`}
                    title="Delete"
                    onClick={(e) => {
                      e.stopPropagation();
                      remove(it);
                    }}
                    className="p-1 rounded hover:bg-white/10"
                  >
                    <Trash2 size={11} className="text-red-400" />
                  </button>
                )}
              </div>
            </div>
          );
        })}
        {items.length === 0 && (
          <div className="col-span-4 text-center text-white/40 text-sm py-12">
            Empty folder · drag &amp; drop files here or click <b>Upload</b>
          </div>
        )}
      </div>

      {/* HEXA Shield upload status card — appears below the grid while a file is being processed */}
      {uploadStatus && (
        <div
          data-testid="files-upload-status"
          className="mx-4 mb-4 mt-1 p-3 flex items-center gap-3 relative overflow-hidden"
          style={{
            background: "linear-gradient(180deg, #f8fafc, #ffffff)",
            border: "1px solid rgba(52,211,153,0.55)",
            boxShadow: "0 0 26px rgba(52,211,153,0.18), 0 12px 30px rgba(0,0,0,0.45)",
            borderRadius: 4,
          }}
        >
          {/* file icon */}
          <div
            className="grid place-items-center flex-shrink-0"
            style={{
              width: 44,
              height: 56,
              borderRadius: 4,
              background: "linear-gradient(160deg, #a78bfa, #8b5cf6)",
              boxShadow: "0 6px 18px rgba(139,92,246,0.45), inset 0 1px 0 rgba(255,255,255,0.18)",
            }}
          >
            {React.createElement(iconFor({ type: "file", mime: uploadStatus.mime }), { size: 22, color: "#fff", strokeWidth: 1.6 })}
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-white text-[14px] truncate" style={{ fontFamily: 'Georgia, "Times New Roman", serif', fontStyle: "italic" }}>
              {uploadStatus.name}
            </div>
            <div className="mt-0.5 mono text-[10.5px] uppercase tracking-[0.18em]" style={{ color: "#34d399", textShadow: "0 0 6px rgba(52,211,153,0.55)" }}>
              {uploadPct < 100 ? "scanning" : "scanned"} · routing to → {uploadStatus.dest}/
            </div>
            {/* progress bar */}
            <div className="mt-2 h-[6px] rounded-full" style={{ background: "rgba(255,255,255,0.06)" }}>
              <div
                className="h-[6px] rounded-full transition-all"
                style={{
                  width: `${uploadPct}%`,
                  background: "linear-gradient(120deg, #34d399, #60a5fa)",
                  boxShadow: "0 0 10px rgba(52,211,153,0.7)",
                }}
              />
            </div>
          </div>
          <div className="mono text-[12px] text-white/65 flex-shrink-0">
            {fmtBytes(uploadStatus.size)}
          </div>
        </div>
      )}

      {/* Upload overlay (tiny status pill — kept for redundancy when status card is off) */}
      {uploading && !uploadStatus && (
        <div className="absolute bottom-3 left-3 right-3 px-3 py-2 rounded-md bg-black/70 border border-white/10 text-xs flex items-center gap-2">
          <Loader2 size={12} className="animate-spin" /> Uploading… {uploadPct}%
        </div>
      )}

      {/* Move dialog */}
      {moveTarget && (
        <div
          className="absolute inset-0 bg-black/70 flex items-center justify-center z-10 p-6"
          onClick={() => {
            setMoveTarget(null);
            setMoveDirs([]);
          }}
        >
          <div
            className="glass rounded-xl w-full max-w-md p-4 flex flex-col gap-2"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-1">
              <span className="text-sm font-medium">
                Move &quot;{moveTarget.name}&quot; to…
              </span>
              <button
                onClick={() => {
                  setMoveTarget(null);
                  setMoveDirs([]);
                }}
                className="btn-ghost text-xs p-1"
                data-testid="move-dialog-close"
              >
                <X size={12} />
              </button>
            </div>
            <button
              data-testid="move-target-root"
              onClick={() => doMove(null)}
              className="flex items-center gap-2 px-3 py-2 rounded hover:bg-white/5 text-sm text-left"
            >
              <Home size={14} /> Home (root)
            </button>
            <div className="max-h-[50vh] overflow-y-auto flex flex-col">
              {moveDirs.length === 0 && (
                <div className="text-xs text-white/40 px-3 py-2">
                  No other folders. Create one first.
                </div>
              )}
              {moveDirs.map((f) => (
                <button
                  key={f.id}
                  data-testid={`move-target-${f.id}`}
                  onClick={() => doMove(f.id)}
                  className="flex items-center gap-2 px-3 py-2 rounded hover:bg-white/5 text-sm text-left"
                >
                  <FolderClosed size={14} color="#a78bfa" /> {f.name}
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* File viewer (text files only) */}
      {openFile && (
        <div
          className="absolute inset-0 bg-black/70 flex items-center justify-center z-10 p-6"
          onClick={() => setOpenFile(null)}
        >
          <div
            className="glass rounded-xl w-full max-w-2xl max-h-[80%] flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between px-4 py-2 border-b border-white/10">
              <span className="text-sm font-medium">{openFile.name}</span>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => onDownload(openFile)}
                  className="btn-ghost text-xs flex items-center gap-1"
                  data-testid="file-viewer-download"
                >
                  <Download size={11} /> Download
                </button>
                <button
                  onClick={() => setOpenFile(null)}
                  className="btn-ghost text-xs"
                  data-testid="file-viewer-close"
                >
                  Close
                </button>
              </div>
            </div>
            {(openFile.mime || "").startsWith("image/") ? (
              <div className="flex-1 grid place-items-center p-4 overflow-auto">
                <img
                  alt={openFile.name}
                  src={filesApi.downloadUrl(openFile.id)}
                  className="max-w-full max-h-[60vh] object-contain"
                />
              </div>
            ) : (openFile.mime || "").startsWith("video/") ? (
              <video
                controls
                src={filesApi.downloadUrl(openFile.id)}
                className="flex-1 w-full bg-black"
              />
            ) : (openFile.mime || "").startsWith("audio/") ? (
              <div className="p-6">
                <audio
                  controls
                  src={filesApi.downloadUrl(openFile.id)}
                  className="w-full"
                />
              </div>
            ) : (openFile.mime || "") === "application/pdf" ? (
              <iframe
                title={openFile.name}
                src={filesApi.downloadUrl(openFile.id)}
                className="flex-1 w-full bg-white"
              />
            ) : (
              <textarea
                data-testid="file-content"
                value={openFile.content || ""}
                onChange={(e) =>
                  setOpenFile({ ...openFile, content: e.target.value })
                }
                onBlur={async () => {
                  await filesApi.update(openFile.id, {
                    name: openFile.name,
                    type: "file",
                    parent: openFile.parent,
                    content: openFile.content,
                    mime: openFile.mime,
                  });
                }}
                className="flex-1 bg-transparent p-4 outline-none text-sm resize-none"
              />
            )}
          </div>
        </div>
      )}

      {/* Share dialog */}
      {shareTarget && (
        <ShareDialog
          target={shareTarget}
          onClose={() => setShareTarget(null)}
          onChanged={async () => { await load(); }}
        />
      )}
    </div>
  );
}

function ShareDialog({ target, onClose, onChanged }) {
  const [addr, setAddr] = useState("");
  const [perm, setPerm] = useState("read");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [local, setLocal] = useState(target);

  const add = async (e) => {
    e?.preventDefault?.();
    setErr("");
    const raw = (addr || "").trim();
    if (!raw) return;
    // Allow "@navjot", bare "navjot", or full email
    let address = raw;
    if (address.startsWith("@")) address = `${address.slice(1)}@hexawebos.com`;
    else if (!address.includes("@")) address = `${address}@hexawebos.com`;
    setBusy(true);
    try {
      const updated = await filesApi.share(target.id, address, perm);
      setLocal(updated);
      setAddr("");
      await onChanged?.();
    } catch (ex) {
      setErr(ex?.response?.data?.detail || "Share failed");
    }
    setBusy(false);
  };

  const remove = async (address) => {
    setBusy(true);
    try {
      await filesApi.unshare(target.id, address);
      const next = { ...local, shared_with: (local.shared_with || []).filter(s => s.address !== address) };
      setLocal(next);
      await onChanged?.();
    } catch (ex) {
      setErr(ex?.response?.data?.detail || "Unshare failed");
    }
    setBusy(false);
  };

  return (
    <div className="absolute inset-0 bg-black/70 flex items-center justify-center z-20 p-6" onClick={onClose}>
      <div className="glass rounded-xl w-full max-w-md p-4 flex flex-col gap-3" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between">
          <div className="text-sm font-medium flex items-center gap-2">
            <Share2 size={14} className="text-[#a78bfa]" />
            Share &quot;{target.name}&quot;
          </div>
          <button onClick={onClose} data-testid="share-close" className="btn-ghost text-xs p-1"><X size={12} /></button>
        </div>
        <form onSubmit={add} className="flex items-center gap-2">
          <input
            data-testid="share-address"
            value={addr}
            onChange={(e) => setAddr(e.target.value)}
            placeholder="@navjot or someone@hexawebos.com"
            className="flex-1 bg-white/5 border border-white/10 rounded px-2 py-1.5 text-sm"
          />
          <select
            data-testid="share-perm"
            value={perm}
            onChange={(e) => setPerm(e.target.value)}
            className="bg-white/5 border border-white/10 rounded px-2 py-1.5 text-sm"
          >
            <option value="read">read</option>
            <option value="edit">edit</option>
          </select>
          <button
            data-testid="share-add"
            type="submit"
            disabled={busy || !addr.trim()}
            className="btn-ghost text-xs px-3 py-1.5"
            style={{ border: "1px solid rgba(139,92,246,0.55)", color: "#c7c1ff" }}
          >
            {busy ? <Loader2 size={11} className="animate-spin" /> : "Add"}
          </button>
        </form>
        {err && <div className="text-[11px] text-[#f87171] mono">{err}</div>}
        <div className="text-[10px] mono uppercase tracking-widest text-white/40">// shared with</div>
        <div className="flex flex-col gap-1.5 max-h-[40vh] overflow-y-auto">
          {((local.shared_with) || []).length === 0 && (
            <div className="text-xs text-white/40 py-2">Not shared with anyone yet.</div>
          )}
          {(local.shared_with || []).map((s) => (
            <div
              key={s.address}
              data-testid={`share-row-${s.address}`}
              className="flex items-center justify-between px-2 py-1.5 rounded bg-white/3 text-xs"
            >
              <div className="flex items-center gap-2">
                <Users size={11} className="text-[#a78bfa]" />
                <span className="mono">{s.address.replace(/@hexawebos\.com$/, "")}</span>
                <span
                  className="text-[9px] mono uppercase tracking-wider px-1.5 py-0.5 rounded-full"
                  style={{
                    background: s.perm === "edit" ? "rgba(52,211,153,0.18)" : "rgba(96,165,250,0.18)",
                    color: s.perm === "edit" ? "#34d399" : "#60a5fa",
                  }}
                >
                  {s.perm}
                </span>
              </div>
              <button
                data-testid={`share-remove-${s.address}`}
                onClick={() => remove(s.address)}
                className="text-red-400 hover:bg-white/5 p-1 rounded"
                title="Remove access"
              >
                <X size={11} />
              </button>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
